/**
 * ModelNode — the small round disc that hangs off every node that runs a model.
 *
 * The model an agent runs on is chosen PER NODE, on the graph (n8n-style): each
 * model-running node carries one of these, and clicking it opens the vendor +
 * model + key picker. There is no agent-wide "default model" any more — one
 * value cannot describe a graph whose nodes deliberately run different vendors
 * (a cheap triage tier next to an expensive reviewer).
 *
 * Purely presentational. Everything it shows comes from `data.state`, which the
 * caller derives with `nodeModelState` — the one place that decides, from data
 * alone, what a node's model state IS, so the disc, the trigger gate and the
 * sub-agent readiness check can never disagree.
 *
 *   locked → the TEMPLATE fixes this node's model (a declared `agent:`, or code
 *            that passes its own `model:` into invokeAgent). The MODEL is
 *            read-only — we never render a control that would not take effect
 *            — but the KEY is not: the template chose which model runs here,
 *            it cannot pay for it. So a locked disc still opens, and goes red
 *            (`missingKey`) when its vendor has no credential, because that
 *            node is exactly as dead as an unset one.
 *   ready  → picked, and a credential exists for its vendor.
 *   nokey  → picked, but nothing can authenticate it. Red; the run would fail.
 *   unset  → nothing picked yet.
 */
import React from 'react';
import { Handle, Position } from 'reactflow';
import { IonIcon } from '@ionic/react';
import { lockClosedOutline, addOutline, sparklesOutline } from 'ionicons/icons';
import IntegrationLogo from '../../shared/IntegrationLogo';
import './ModelNode.css';

// Model VENDOR → the brand mark in IntegrationLogo (the marks SSOT).
const VENDOR_LOGO = { claude: 'anthropic', codex: 'openai', gemini: 'gemini' };

export const ModelNode = ({ data }) => {
  const state = data?.state || { kind: 'unset', label: 'Set model' };
  const { kind, vendor, label, sub, missingKey } = state;
  const logo = VENDOR_LOGO[vendor];
  const locked = kind === 'locked';
  // A fixed node with a key for its vendor is as runnable as any other, so it
  // earns the same green. Not when the vendor is unknowable, though — we'd be
  // calling a node ready without knowing what would authenticate it.
  const lockedAndReady = locked && !!vendor && !missingKey;

  return (
    <div
      className={[
        'gwe-model-node',
        `gwe-model-node--${kind}`,
        missingKey && 'is-missingkey',
        lockedAndReady && 'is-ready',
        data?.flagged && 'is-flagged',
      ].filter(Boolean).join(' ')}
      title={data?.tooltip || undefined}
    >
      {/* Target only: the connector comes FROM its node's free BOTTOM handle
          (the flow itself runs left→right, so bottom is never in use). The
          disc is a leaf — it never feeds the flow. */}
      <Handle
        type="target"
        position={Position.Left}
        id="disc"
        isConnectable={false}
        className="gwe-model-node__handle"
      />
      <div className="gwe-model-node__disc">
        {logo ? (
          <IntegrationLogo id={logo} size={22} />
        ) : (
          <IonIcon icon={locked ? sparklesOutline : addOutline} />
        )}
      </div>
      <div className="gwe-model-node__caption">
        <div className="gwe-model-node__label">
          {locked && (
            <IonIcon className="gwe-model-node__lock" icon={lockClosedOutline} aria-label="Fixed by the template" />
          )}
          <span>{label}</span>
        </div>
        {sub ? <div className="gwe-model-node__sub">{sub}</div> : null}
      </div>
    </div>
  );
};

export default ModelNode;
